/**
 * useTaskStatus - Status and dependency change operations
 */
import { getStatusCategory } from '../../FlowchartView/utils/statusUtils';
import { getAllConditionTaskIds } from '../../shared/ConditionsDropdown/utils/conditionHelpers';

export const useTaskStatus = (localSprint, setLocalSprint, recordAction) => {
  // Check if we're in template mode - templates don't auto-set startedAt/completedAt
  // because those dates should be set when a campaign is created from the template
  const isTemplate = localSprint?.isTemplate === true;

  // kanbanOrder: the index position where the task should appear within its status column
  // Similar to how position (x, y) works for Flow view, kanbanOrder persists card position in Kanban
  const handleTaskStatusChange = (taskId, newStatus, blockedReason = null, kanbanOrder = null) => {
    // Capture old values for undo
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldStatus = task?.status;
    const oldBlockedReason = task?.blockedReason;
    const oldKanbanOrder = task?.kanbanOrder;
    
    // Get status categories to determine if transitioning to in_progress or completed
    const statuses = localSprint?.statuses;
    const newCategory = getStatusCategory(newStatus, statuses);
    const oldCategory = task ? getStatusCategory(task.status, statuses) : null;

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      
      // If kanbanOrder is specified, we need to update other tasks' orders in the column
      let updatedTasks = tasks;
      
      if (kanbanOrder !== null && kanbanOrder !== undefined) {
        // Get tasks in the target status, sorted by current kanbanOrder
        const tasksInTargetStatus = tasks
          .filter(t => t.status === newStatus)
          .sort((a, b) => (a.kanbanOrder ?? Infinity) - (b.kanbanOrder ?? Infinity));
        
        // Find the moving task's current position in the sorted list
        const movingTaskCurrentIndex = tasksInTargetStatus.findIndex(t => t.id === taskId);
        const isMovingWithinSameColumn = task?.status === newStatus;
        
        // Normalize kanbanOrder: rebuild the order from scratch
        // This is cleaner than trying to shift individual values
        const otherTasksInColumn = tasksInTargetStatus.filter(t => t.id !== taskId);
        
        // Insert the moving task at the new position
        const newOrderedTasks = [...otherTasksInColumn];
        // Clamp the index to valid range
        const insertIndex = Math.max(0, Math.min(kanbanOrder, newOrderedTasks.length));
        newOrderedTasks.splice(insertIndex, 0, { id: taskId });
        
        // Create a map of taskId -> new kanbanOrder
        const newOrderMap = {};
        newOrderedTasks.forEach((t, idx) => {
          newOrderMap[t.id] = idx;
        });
        
        // Apply the new orders
        updatedTasks = tasks.map(t => {
          if (t.id === taskId) {
            const now = new Date().toISOString();
            // This task gets the new kanbanOrder and status
            const updates = {
              ...t,
              status: newStatus,
              kanbanOrder: newOrderMap[taskId],
              updatedAt: now,
            };
            
            // Handle startedAt - set current time when entering the in_progress category
            // In template mode, don't auto-set startedAt - it will be set when campaign is created
            if (newCategory === 'in_progress' && !isTemplate && oldCategory !== 'in_progress') {
              updates.startedAt = now;
            }
            
            // Handle completedAt
            // In template mode, don't auto-set completedAt - it will be set when campaign is created
            if (newCategory === 'completed' && !isTemplate) {
              updates.completedAt = now;
            } else if (!isTemplate) {
              updates.completedAt = null;
            }
            
            if (newStatus === 'blocked') {
              updates.blockedReason = blockedReason || t.blockedReason || null;
              updates.blockedAt = now;
            } else {
              updates.blockedReason = null;
              updates.blockedAt = null;
            }
            
            return updates;
          }
          
          // For other tasks in the same target status, update their kanbanOrder
          if (t.status === newStatus && newOrderMap[t.id] !== undefined) {
            return { ...t, kanbanOrder: newOrderMap[t.id] };
          }
          
          return t;
        });
      } else {
        // No kanbanOrder specified - just update status
        updatedTasks = tasks.map(t => {
          if (t.id === taskId) {
            const now = new Date().toISOString();
            const updates = {
              ...t,
              status: newStatus,
              updatedAt: now,
            };
            
            // Handle startedAt - set current time when entering the in_progress category
            // In template mode, don't auto-set startedAt - it will be set when campaign is created
            if (newCategory === 'in_progress' && !isTemplate && oldCategory !== 'in_progress') {
              updates.startedAt = now;
            }
            
            // Handle completedAt
            // In template mode, don't auto-set completedAt - it will be set when campaign is created
            if (newCategory === 'completed' && !isTemplate) {
              updates.completedAt = now;
            } else if (!isTemplate) {
              updates.completedAt = null;
            }
            
            if (newStatus === 'blocked') {
              updates.blockedReason = blockedReason || t.blockedReason || null;
              updates.blockedAt = now;
            } else {
              updates.blockedReason = null;
              updates.blockedAt = null;
            }
            
            return updates;
          }
          return t;
        });
      }

      return { ...prev, tasks: updatedTasks };
    });

    // Record for undo (only if status actually changed)
    if (recordAction && oldStatus !== newStatus) {
      const taskName = task?.name || 'Task';
      const formatStatus = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Unknown';
      recordAction({
        type: 'CHANGE_STATUS',
        description: `${taskName}: ${formatStatus(oldStatus)} → ${formatStatus(newStatus)}`,
        undo: () => handleTaskStatusChange(taskId, oldStatus, oldBlockedReason, oldKanbanOrder),
        redo: () => handleTaskStatusChange(taskId, newStatus, blockedReason, kanbanOrder),
      });
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DEPRECATED: handleDependencyChange - Use handleConditionsChange instead
  // The old dependsOn system has been replaced by unlockConditions
  // ═══════════════════════════════════════════════════════════════
  // const handleDependencyChange = (taskId, dependencyIds) => {
  //   // Capture old dependencies for undo
  //   const task = localSprint?.tasks?.find(t => t.id === taskId);
  //   const oldDependencies = task?.dependsOn || [];
  //
  //   setLocalSprint(prev => {
  //     if (!prev) return prev;
  //     const tasks = prev.tasks || [];
  //     
  //     const updatedTasks = tasks.map(task => {
  //       if (task.id === taskId) {
  //         return { ...task, dependsOn: dependencyIds };
  //       }
  //       return task;
  //     });
  //
  //     return { ...prev, tasks: updatedTasks };
  //   });
  //
  //   // Record for undo
  //   if (recordAction) {
  //     const taskName = task?.name || 'Task';
  //     const oldCount = oldDependencies.length;
  //     const newCount = dependencyIds.length;
  //     recordAction({
  //       type: 'CHANGE_DEPENDENCIES',
  //       description: `${taskName}: ${oldCount} → ${newCount} dependencies`,
  //       undo: () => handleDependencyChange(taskId, oldDependencies),
  //       redo: () => handleDependencyChange(taskId, dependencyIds),
  //     });
  //   }
  // };
  
  // Stub for backward compatibility - throws error if called
  const handleDependencyChange = () => {
    throw new Error('handleDependencyChange is DEPRECATED. Use handleConditionsChange instead.');
  };

  /**
   * Handle unlock conditions change (advanced AND/OR rules)
   * Conditions are stored as an array of { taskId, operator } objects
   * 
   * IMPORTANT: When setting unlockConditions, we also clear dependsOn to complete
   * the migration from the old system. This ensures edge generation uses the new
   * conditions as the source of truth.
   */
  const handleConditionsChange = (taskId, conditions, options = {}) => {
    // Capture old state for undo
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldConditions = task?.unlockConditions || [];
    const oldDependsOn = task?.dependsOn || [];

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      
      const updatedTasks = tasks.map(task => {
        if (task.id === taskId) {
          // Set new conditions AND clear old dependsOn to complete migration
          return { 
            ...task, 
            unlockConditions: conditions,
            dependsOn: [], // Clear legacy dependsOn - unlockConditions is now source of truth
          };
        }
        return task;
      });

      return { ...prev, tasks: updatedTasks };
    });

    const applyConditions = () => {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t =>
            t.id === taskId
              ? { ...t, unlockConditions: conditions, dependsOn: [] }
              : t
          ),
        };
      });
    };

    const restoreConditions = () => {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t =>
            t.id === taskId
              ? { ...t, unlockConditions: oldConditions, dependsOn: oldDependsOn }
              : t
          ),
        };
      });
    };

    const applyPositions = (positionMap) => {
      if (!positionMap || Object.keys(positionMap).length === 0) return;
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t => {
            const pos = positionMap[t.id];
            return pos ? { ...t, x: pos.x, y: pos.y } : t;
          }),
        };
      });
    };

    // Defer undo until after auto-tidy (flowchart connection drag)
    if (options.skipRecordAction && recordAction) {
      const taskName = task?.name || 'Task';
      return {
        recordCombinedAction: (originalPositions, currentTasks) => {
          const newPositions = {};
          if (originalPositions && currentTasks?.length) {
            Object.keys(originalPositions).forEach((id) => {
              const t = currentTasks.find((task) => task.id === id);
              if (t && Number.isFinite(t.x) && Number.isFinite(t.y)) {
                newPositions[id] = { x: t.x, y: t.y };
              }
            });
          }

          recordAction({
            type: 'ADD_CONNECTION',
            description: `Connect: "${taskName}"`,
            undo: () => {
              restoreConditions();
              applyPositions(originalPositions);
            },
            redo: () => {
              applyConditions();
              applyPositions(newPositions);
            },
          });
        },
      };
    }

    // Record for undo
    if (recordAction) {
      const taskName = task?.name || 'Task';
      const oldCount = getAllConditionTaskIds(oldConditions).length;
      const newCount = getAllConditionTaskIds(conditions).length;
      recordAction({
        type: 'CHANGE_CONDITIONS',
        description: `${taskName}: ${oldCount} → ${newCount} unlock conditions`,
        undo: restoreConditions,
        redo: applyConditions,
      });
    }
  };

  return {
    handleTaskStatusChange,
    handleDependencyChange,
    handleConditionsChange,
  };
};

export default useTaskStatus;

